import {DataStatus, Alert} from 'platform/components';
import {Show, Space, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerPaymentQuery} from '@dms/api/finance';
import {
  PaymentAllowedAction,
  useGetCheckoutByIdQuery,
  useGetCheckoutPaymentByIdQuery,
  useGetCheckoutPaymentIssueRestrictionQuery,
} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {useNavigate} from 'shared/web';

import {getAction, getMessage, shouldShowAlert} from '../constants/issueRestrictionAlerts';
import {CheckoutPaymentCardProps} from '../types/CheckoutPaymentCardProps';
import {CheckoutPaymentCardContent} from './CheckoutPaymentCardContent';

const ISSUE_ACTIONS: PaymentAllowedAction[] = [
  'ISSUE_INVOICE',
  'ISSUE_FULL_CORRECTION',
  'ISSUE_PARTIAL_CORRECTION',
];

export function CheckoutPaymentCard(props: CheckoutPaymentCardProps) {
  const navigate = useNavigate();
  const {
    data: paymentData,
    isLoading: isPaymentLoading,
    isError: isPaymentError,
  } = useGetCheckoutPaymentByIdQuery({
    checkoutId: props.checkoutId,
    paymentId: props.paymentId,
  });

  const {
    data: checkoutData,
    isLoading: isCheckoutLoading,
    isError: isCheckoutError,
  } = useGetCheckoutByIdQuery({checkoutId: props.checkoutId});

  const {
    data: customerPayment,
    isFetching: isCustomerPaymentFetching,
    isError: isCustomerPaymentError,
  } = useGetCustomerPaymentQuery({customerId: props.customerId});

  const allowedActions = paymentData?.uiCapabilities?.allowedActions ?? [];

  const canIssue = ISSUE_ACTIONS.some((action) => allowedActions.includes(action));

  const {
    data: issueRestrictions,
    isLoading: isIssueRestrictionsLoading,
    isError: isIssueRestrictionsError,
  } = useGetCheckoutPaymentIssueRestrictionQuery(
    {checkoutId: props.checkoutId, paymentId: props.paymentId},
    {
      refetchOnMountOrArgChange: true,
      skip: !canIssue,
    }
  );

  const isLoading =
    isPaymentLoading ||
    isCustomerPaymentFetching ||
    isCheckoutLoading ||
    isIssueRestrictionsLoading;
  const isError =
    isPaymentError || isCustomerPaymentError || isCheckoutError || isIssueRestrictionsError;

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={100}>
      <Show when={isNotNilOrEmpty(issueRestrictions)}>
        <VStack spacing={2}>
          {issueRestrictions?.map((restriction) => {
            if (!shouldShowAlert(restriction.code, props.contextTarget)) {
              return null;
            }

            const message = getMessage(restriction.code);
            const action = getAction(
              restriction.code,
              props.contextId,
              props.contextTarget,
              props.actionContext
            );

            return (
              <Alert
                key={restriction.code}
                title={message}
                variant="error"
                type="banner"
                hyperlinks={
                  isNotNil(action) && isNotNil(action.href)
                    ? [
                        {
                          title: action.title ?? i18n.t('general.actions.view'),
                          onClick: () => {
                            if (isNotNil(action.href)) {
                              navigate(action.href);
                            }
                          },
                        },
                      ]
                    : undefined
                }
              />
            );
          })}
        </VStack>
        <Space vertical={2} />
      </Show>
      <Show when={paymentData}>
        {(paymentData) => (
          <CheckoutPaymentCardContent
            paymentId={props.paymentId}
            checkoutId={props.checkoutId}
            customerId={props.customerId}
            defaultPaymentType={props.defaultPaymentType}
            isEditingDisabled={props.isEditingDisabled}
            isCompletingDisabled={props.isCompletingDisabled}
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            onAddAnotherDepositClick={props.onAddAnotherDepositClick}
            onRemoveDepositClick={props.onRemoveDepositClick}
            isBalancePaymentIssued={props.isBalancePaymentIssued}
            contextTarget={props.contextTarget}
            contextId={props.contextId}
            actionContext={props.actionContext}
            payment={paymentData}
            checkout={checkoutData}
            customerPayment={customerPayment}
            hasIssueRestrictions={isNotNilOrEmpty(issueRestrictions)}
            data-testid={props['data-testid']}
          />
        )}
      </Show>
    </DataStatus>
  );
}
